import { DialogCloseEvent } from '@theme/dialog';
import { clamp, preventDefault, isMobileBreakpoint } from '@theme/utilities';
import { Component } from '@theme/component';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
/* NIKA: Amazon-стил галерия — снимките в списъка стоят на 1x,
   двойният тап зумва до 2x; ръбът на снимката при влачене освобождава зума */
const DEFAULT_ZOOM = 2;
const INITIAL_ZOOM = 1;
/* NIKA: ластичен ръб — опъване след края с нарастващо съпротивление
   (asymptote RUBBER_MAX_PX), пружиниране назад при пускане; дърпане
   отвъд EDGE_CLOSE_PX сурово разстояние = настоятелно → зумът се затваря */
const RUBBER_MAX_PX = 220;
const RUBBER_SOFT_PX = 200;
const EDGE_CLOSE_PX = 210;
/* NIKA: истинско "драсване" = кратко (<200ms), дълго (>200px), бързо
   (>1.4 px/ms) и право (директно разстояние ≥ 88% от изминатия път) */
const FLICK_MAX_MS = 200;
const FLICK_MIN_PX = 200;
const FLICK_MIN_VELOCITY = 1.4;
const FLICK_MIN_STRAIGHT = 0.88;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 10;

/**
 * @typedef {object} Refs
 * @property {HTMLImageElement} image - The image element to zoom and drag.
 */

/** @extends {Component<Refs>} */
export class DragZoomWrapper extends Component {
  requiredRefs = ['image'];
  #controller = new AbortController();
  /** @type {number} */
  #scale = INITIAL_ZOOM;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = INITIAL_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #initialized = false;
  /** @type {number | null} */
  #animationFrame = null;
  /** @type {number} */
  #lastTapTime = 0;
  /** @type {Point | null} */
  #lastTapPosition = null;

  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;

  /** @type {boolean} */
  #hasManualZoom = false;

  /** NIKA: активен pinch — без кламп на границите, следваме пръстите */
  #isPinching = false;

  get #image() {
    return this.refs.image;
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.#image) return;

    this.#initResizeListener();
    window.addEventListener(DialogCloseEvent.eventName, this.#resetZoom);

    if (!isMobileBreakpoint()) return;

    this.#initEventListeners();
    this.#updateTransform();

    /* NIKA (mobile): в галерията снимката се крапва визуално до реалното
       съдържание (белите полета на файла НЕ броят) — плътно по екрана,
       Amazon стил. Анализът е същият като за пан границите. */
    this.#nikaApplyListCrop();
  }

  /* NIKA: CSS vars за content-crop на списъчната снимка. Смята се след
     като снимката е декодирана; при цял файл съдържание — нищо. */
  async #nikaApplyListCrop() {
    if (!this.closest('.dialog-zoomed-gallery')) return;
    await this.#nikaAnalyzeContent();
    const box = this._nikaContentBox;
    const li = this.closest('li');
    if (!box || !li) return;
    const fW = box.fRight - box.fLeft;
    const fH = box.fBot - box.fTop;
    if (fW >= 0.98 && fH >= 0.98) return; /* файлът е плътен — нищо */
    /* файлов аспект (h/w) от --ratio (w/h) на медията */
    const ratio = parseFloat(this.closest('[style*="--ratio"]')?.style.getPropertyValue('--ratio') || li.style.getPropertyValue('--ratio')) || 1;
    const fileH = 1 / ratio; /* височина във W единици */
    const k = 1 / fW;
    const cropH = fH * fileH * k; /* височина на li във W единици */
    const cx = (box.fLeft + box.fRight) / 2;
    const cy = (box.fTop + box.fBot) / 2;
    const tx = 0.5 - k * cx; /* във W единици */
    const ty = cropH / 2 - k * cy * fileH;
    li.style.setProperty('--nika-crop-k', k.toFixed(4));
    /* wrapper-ът държи височината чрез aspect-ratio (w/h) — height в %
       не работи (би бил спрямо родителска височина, не ширина) */
    li.style.setProperty('--nika-crop-ar', (1 / cropH).toFixed(4));
    /* translate % са спрямо СОБСТВЕНИЯ размер на елемента (W x fileH*W) */
    li.style.setProperty('--nika-crop-tx', (tx * 100).toFixed(2) + '%');
    li.style.setProperty('--nika-crop-ty', ((ty / fileH) * 100).toFixed(2) + '%');
    li.setAttribute('data-nika-crop', '');
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);

    // Initialize transform immediately
    this.#updateTransform();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(DialogCloseEvent.eventName, this.#resetZoom);
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #handleResize = () => {
    if (!this.#initialized && isMobileBreakpoint()) {
      this.#initEventListeners();
    }

    if (this.#initialized) {
      this.#requestUpdateTransform();
    }
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    const touchCount = event.touches.length;

    /* NIKA: неувеличена снимка + 1 пръст → списъкът скролва нативно.
       Записваме tap-а само за double-tap детекция. */
    if (touchCount === 1 && this.#scale <= 1.01) {
      const touch = event.touches[0];
      if (!touch) return;
      const currentTime = performance.now();
      const timeSinceLastTap = currentTime - this.#lastTapTime;
      if (timeSinceLastTap < DOUBLE_TAP_DELAY && this.#lastTapPosition &&
          getDistance(touch, this.#lastTapPosition) < DOUBLE_TAP_DISTANCE) {
        preventDefault(event);
        this.#handleDoubleTapFromTouch(touch);
        this.#lastTapTime = 0;
        this.#lastTapPosition = null;
        return;
      }
      this.#storeTapInfo(currentTime, touch);
      return;
    }

    preventDefault(event);

    if (touchCount === 2) {
      // Early exit if touches are invalid
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (!touch1 || !touch2) return;

      // Avoid object allocation by passing touches directly
      this.#startZoomGestureFromTouches(touch1, touch2);
    } else if (touchCount === 1) {
      const touch = event.touches[0];
      if (!touch) return;

      // Use performance.now() for better precision and performance
      const currentTime = performance.now();
      const timeSinceLastTap = currentTime - this.#lastTapTime;

      // Early exit if too much time has passed
      if (timeSinceLastTap >= DOUBLE_TAP_DELAY) {
        this.#storeTapInfo(currentTime, touch);
        this.#startDragGestureFromTouch(touch);
        return;
      }

      // Only check distance if we have a previous tap within time window
      if (this.#lastTapPosition) {
        // Distance calculation with early exit
        const distance = getDistance(touch, this.#lastTapPosition);

        if (distance < DOUBLE_TAP_DISTANCE) {
          // This is a double-tap, handle zoom toggle
          this.#handleDoubleTapFromTouch(touch);
          this.#lastTapTime = 0; // Reset to prevent triple-tap
          this.#lastTapPosition = null;
          return;
        }
      }

      // Store tap info for potential double-tap detection
      this.#storeTapInfo(currentTime, touch);
      this.#startDragGestureFromTouch(touch);
    }
  };

  /**
   * Start a zoom gesture with two touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #startZoomGestureFromTouches(touch1, touch2) {
    /* NIKA: влизаме в цял екран ОЩЕ при допира, но с начален мащаб и
       позиция, които показват снимката ТОЧНО както в списъка — визуален
       континюитет: няма скок, пръстите остават върху същата точка и
       зумът тръгва оттам (Amazon поведение). */
    const wasFull = this.classList.contains('nika-zoom-full');
    const pre = this.getBoundingClientRect();
    this.#enterFull();
    if (!wasFull && this.classList.contains('nika-zoom-full')) {
      const post = this.getBoundingClientRect();
      const img = this.#image;
      const nw = (img && img.naturalWidth) || pre.width || 1;
      const nh = (img && img.naturalHeight) || pre.width || 1;
      const sCov = Math.max(pre.width / nw, pre.height / nh);
      const sCon = Math.min(post.width / nw, post.height / nh) || 1;
      const z0 = clamp(sCov / sCon, MIN_ZOOM, MAX_ZOOM);
      const preCx = pre.left + pre.width / 2;
      const preCy = pre.top + pre.height / 2;
      const postCx = post.left + post.width / 2;
      const postCy = post.top + post.height / 2;
      this.#scale = z0;
      this.#translate.x = (preCx - postCx) / z0;
      this.#translate.y = (preCy - postCy) / z0;
      this._nikaEntry = { s: z0, x: this.#translate.x, y: this.#translate.y };
      this.#requestUpdateTransform();
    }
    // Calculate initial distance between touches
    this.#initialDistance = getDistance(touch1, touch2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
    this.#isPinching = true;
  }

  /**
   * Start a drag gesture with a single touch
   * @param {Touch} touch
   */
  #startDragGestureFromTouch(touch) {
    this.#startPosition = { x: touch.clientX, y: touch.clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;
    this._nikaDragStart = performance.now();
    this._nikaRubberY = 0;
    this._nikaPathLen = 0;
    this._nikaLastPt = { clientX: touch.clientX, clientY: touch.clientY };
  }

  /**
   * Store tap information for double-tap detection
   * @param {number} currentTime
   * @param {Touch} touch
   */
  #storeTapInfo(currentTime, touch) {
    this.#lastTapTime = currentTime;
    this.#lastTapPosition = { x: touch.clientX, y: touch.clientY };
  }

  /**
   * Handle double-tap zoom toggle from touch
   * @param {Touch} touch - The touch where the double-tap occurred
   */
  #handleDoubleTapFromTouch(touch) {
    /* NIKA: двойният тап НЕ зумва (както при Amazon — бъгваше);
       работи само за ИЗХОД от зум обратно към галерията */
    if (this.#scale > 1.01) {
      this.#animatedExit();
    }
    /* при неувеличена снимка: нищо (нативният zoom е блокиран отделно) */
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    const touchCount = event.touches.length;

    /* NIKA: 1 пръст върху неувеличена снимка → нативен скрол.
       Движението инвалидира tap историята — скрол докосванията не
       образуват фалшив double-tap. */
    if (touchCount === 1 && this.#scale <= 1.01 && !this.#isDragging) {
      this.#lastTapTime = 0;
      this.#lastTapPosition = null;
      return;
    }

    preventDefault(event);

    if (touchCount === 2) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (touch1 && touch2) {
        this.#processZoomGesture(touch1, touch2);
      }
    } else if (touchCount === 1 && this.#isDragging) {
      const touch = event.touches[0];
      if (touch) {
        this.#processDragGesture(touch);
      }
    }
  };

  /**
   * Process zoom gesture from touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #processZoomGesture(touch1, touch2) {
    // Calculate midpoint directly without object allocation
    const midX = (touch1.clientX + touch2.clientX) / 2;
    const midY = (touch1.clientY + touch2.clientY) / 2;

    // Calculate current distance between touches
    const currentDistance = getDistance(touch1, touch2);

    const oldScale = this.#scale;

    // Calculate and apply new scale
    const newScale = (currentDistance / this.#initialDistance) * this.#startScale;
    this.#scale = clamp(newScale, MIN_ZOOM, MAX_ZOOM);

    // Mark that manual zoom has been used
    this.#hasManualZoom = true;

    /* NIKA: пинчът изкарва снимката на цял екран */
    if (this.#scale > 1.05) this.#enterFull();

    // Adjust translation to keep the pinch midpoint stationary
    const containerCenterX = this.clientWidth / 2;
    const containerCenterY = this.clientHeight / 2;

    const distanceFromCenterX = midX - containerCenterX;
    const distanceFromCenterY = midY - containerCenterY;

    // Calculate how the image position needs to change to keep the midpoint stationary
    const scaleDelta = this.#scale / oldScale - 1.0;

    // Apply correction to prevent zooming on the opposite side of the midpoint
    this.#translate.x -= (distanceFromCenterX * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenterY * scaleDelta) / this.#scale;

    this.#requestUpdateTransform();
    this.#isDragging = false;
  }

  /**
   * Process drag gesture from touch
   * @param {Touch} touch
   */
  #processDragGesture(touch) {
    // Check if we've moved beyond the drag threshold
    const distance = getDistance(touch, this.#startPosition);

    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
      // Movement is too small, don't process as drag yet
      return;
    }

    this.#hasDraggedBeyondThreshold = true;

    if (this._nikaLastPt) {
      this._nikaPathLen = (this._nikaPathLen || 0) + getDistance(touch, this._nikaLastPt);
      this._nikaLastPt = { clientX: touch.clientX, clientY: touch.clientY };
    }

    // Calculate movement deltas for translation
    const dx = touch.clientX - this.#startPosition.x;
    const dy = touch.clientY - this.#startPosition.y;

    // Calculate new translation directly
    this.#translate.x = this.#startTranslate.x + dx / this.#scale;
    this.#translate.y = this.#startTranslate.y + dy / this.#scale;

    /* NIKA: ластичен ръб — отвъд края на снимката влаченето среща
       нарастващо съпротивление (демпфиран overscroll, като iOS);
       пуснеш ли — пружинира обратно (виж touchend). Настоятелно
       дърпане отвъд EDGE_CLOSE_PX сурово разстояние затваря зума. */
    if (this.#scale > 1.01) {
      const bounds = this.#nikaTranslateBounds();
      const raw = this.#translate.y;
      const overUnits = raw > bounds.yMax ? raw - bounds.yMax : raw < bounds.yMin ? raw - bounds.yMin : 0;
      if (overUnits !== 0) {
        const overScreen = Math.abs(overUnits) * this.#scale;
        if (overScreen > EDGE_CLOSE_PX) {
          this._nikaRubberY = 0;
          this.#releaseZoom();
          return;
        }
        const damped = (RUBBER_MAX_PX * overScreen) / (overScreen + RUBBER_SOFT_PX);
        this._nikaRubberY = (Math.sign(overUnits) * damped) / this.#scale;
      } else {
        this._nikaRubberY = 0;
      }
    }

    this.#requestUpdateTransform();
  }

  /* NIKA: зумнатата снимка излиза на цял екран (Amazon стил).
     БЕЗ DOM местене — преместването disconnect-ва компонента и убива
     touch listener-ите му (AbortController). Само класове. */
  /* NIKA: публичен reset — ползва се от другите wrappers, за да е
     активен само един зум наведнъж */
  nikaReset = () => {
    this.#resetZoom();
  };

  #enterFull() {
    if (this.classList.contains('nika-zoom-full')) return;
    /* анализът на реалното съдържание тръгва веднага (async, еднократно) */
    this.#nikaAnalyzeContent();
    /* Zoom mode: чист layout, без fixed/absolute (iOS-безопасно) —
       другите снимки се скриват, тази става цял екран, скролът замръзва */
    const dlg = this.closest('dialog');
    const li = this.closest('li');
    if (!dlg || !li) return;
    /* само един активен зум: нулирай всички останали снимки */
    dlg.querySelectorAll('drag-zoom-wrapper').forEach((w) => {
      if (w !== this && typeof w.nikaReset === 'function') w.nikaReset();
    });
    this._nikaPrevScroll = dlg.scrollTop;
    li.classList.add('nika-zoom-li');
    dlg.classList.add('nika-zoom-mode');
    /* моментално, без smooth скрол анимация */
    dlg.style.scrollBehavior = 'auto';
    dlg.scrollTop = 0;
    dlg.style.scrollBehavior = '';
    this.classList.add('nika-zoom-full');
    this.#requestUpdateTransform();
  }

  #exitFull() {
    if (!this.classList.contains('nika-zoom-full')) return;
    this.classList.remove('nika-zoom-full');
    const dlg = this.closest('dialog');
    const li = this.closest('li');
    if (dlg) {
      dlg.classList.remove('nika-zoom-mode');
      if (typeof this._nikaPrevScroll === 'number') {
        /* моментално връщане на позицията — smooth скролът правеше
           видимо движение нагоре-надолу след ънзум */
        dlg.style.scrollBehavior = 'auto';
        dlg.scrollTop = this._nikaPrevScroll;
        dlg.style.scrollBehavior = '';
      }
    }
    if (li) li.classList.remove('nika-zoom-li');
    /* мек fade на списъка — скрива layout смяната */
    if (dlg) {
      dlg.classList.add('nika-zoom-exited');
      setTimeout(() => dlg.classList.remove('nika-zoom-exited'), 350);
    }
    this.#requestUpdateTransform();
  }

  /* NIKA: реалният обхват на СЪДЪРЖАНИЕТО в снимката. Продуктовите
     файлове са квадратни с бял фон — самата стока често е лента в
     средата. Без този анализ границите се смятат по файла и влаченето
     продължава, докато БЯЛОТО стигне ръба на екрана — стоката отдавна
     е излязла. Пикселен анализ веднъж (96x96 canvas), фракции 0..1;
     при грешка/тъмен фон → целият файл (старото поведение). */
  async #nikaAnalyzeContent() {
    if (this._nikaContentBox !== undefined) return;
    this._nikaContentBox = null;
    try {
      let src = this.#image?.currentSrc || this.#image?.src;
      if (!src) return;
      /* кеш: височините на галерията трябва да са стабилни ПРЕДИ
         отварянето (иначе скролът на open() се разминава) — при второ
         зареждане/навигация крапът се прилага мигновено, без мрежа */
      const cacheKey = 'nika-cbox:' + src.split('?')[0].split('/').pop();
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          this._nikaContentBox = JSON.parse(cached) || null;
          return;
        }
      } catch (e) { /* ignore */ }
      /* ДЕТЕРМИНИСТИЧНО: анализът винаги върху една и съща версия на
         файла (width=420) — иначе srcset дава различна резолюция на
         различни устройства и ръбовете варират с 1-2% (изядени пети) */
      if (/[?&]width=\d+/.test(src)) src = src.replace(/([?&])width=\d+/, '$1width=420');
      else src += (src.includes('?') ? '&' : '?') + 'width=420';
      /* Safari CORS-cache бъг: <img crossorigin> за URL, зареден вече
         БЕЗ crossorigin, получава кешираното копие без CORS хедъри →
         canvas taint → крапът тихо липсва (само на iOS). Затова: fetch
         (cache: reload = чиста CORS заявка) → blob → ImageBitmap/blob
         URL — blob-ът е same-origin и canvas-ът никога не е tainted. */
      const res = await fetch(src, { mode: 'cors', cache: 'reload' });
      if (!res.ok) return;
      const blob = await res.blob();
      let source;
      if (typeof createImageBitmap === 'function') {
        source = await createImageBitmap(blob);
      } else {
        const url = URL.createObjectURL(blob);
        try {
          const probe = new Image();
          probe.decoding = 'async';
          probe.src = url;
          await probe.decode();
          source = probe;
        } finally {
          setTimeout(() => URL.revokeObjectURL(url), 5000);
        }
      }
      const N = 96;
      const canvas = document.createElement('canvas');
      canvas.width = N;
      canvas.height = N;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(source, 0, 0, N, N);
      const d = ctx.getImageData(0, 0, N, N).data;
      /* праг 248: меките сенки под продукта (~245-247) са съдържание —
         иначе долният ръб "реже" визуално основата */
      const isBg = (i) => d[i + 3] < 24 || (d[i] > 248 && d[i + 1] > 248 && d[i + 2] > 248);
      let top = N, bot = -1, left = N, right = -1;
      for (let y = 0; y < N; y++) {
        for (let x = 0; x < N; x++) {
          const i = (y * N + x) * 4;
          if (!isBg(i)) {
            if (y < top) top = y;
            if (y > bot) bot = y;
            if (x < left) left = x;
            if (x > right) right = x;
          }
        }
      }
      if (bot < 0 || bot - top < 4 || right - left < 4) return;
      /* малък въздух като Amazon: ~2% до ръба на съдържанието */
      const pad = 2;
      const box = {
        fTop: Math.max(0, (top - pad) / N),
        fBot: Math.min(1, (bot + 1 + pad) / N),
        fLeft: Math.max(0, (left - pad) / N),
        fRight: Math.min(1, (right + 1 + pad) / N),
      };
      /* съдържание, опиращо самия ръб на файла → страната не се крапва */
      const SNAP = 0.03;
      if (box.fTop < SNAP) box.fTop = 0;
      if (1 - box.fBot < SNAP) box.fBot = 1;
      if (box.fLeft < SNAP) box.fLeft = 0;
      if (1 - box.fRight < SNAP) box.fRight = 1;
      this._nikaContentBox = box;
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify(box));
      } catch (e) { /* ignore */ }
    } catch (e) { /* tainted canvas / грешка → целият файл */ }
  }

  /* NIKA: границите на влачене при текущия мащаб. В цял екран
     (nika-zoom-full) wrapper-ът може да е ПО-ШИРОК от екрана (размерът
     идва от aspect ratio на контейнера) — затова границите там са
     АСИМЕТРИЧНИ, спрямо реалния viewport: влачиш, докато срещуположният
     ръб на РЕАЛНОТО съдържание (не файла) опре ръба на екрана. Извън
     цял екран — симетрично около центъра на елемента. */
  #nikaTranslateBounds() {
    const wrapperRect = this.getBoundingClientRect();
    const imageElement = this.#image;
    let naturalWidth, naturalHeight;
    if (imageElement && imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0) {
      naturalWidth = imageElement.naturalWidth;
      naturalHeight = imageElement.naturalHeight;
    } else {
      naturalWidth = wrapperRect.width;
      naturalHeight = wrapperRect.width;
    }
    const imageAspectRatio = naturalWidth / naturalHeight;
    const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;
    let actualImageWidth, actualImageHeight;
    if (imageAspectRatio > wrapperAspectRatio) {
      actualImageWidth = wrapperRect.width;
      actualImageHeight = actualImageWidth / imageAspectRatio;
    } else {
      actualImageHeight = wrapperRect.height;
      actualImageWidth = actualImageHeight * imageAspectRatio;
    }
    const scaledImageWidth = actualImageWidth * this.#scale;
    const scaledImageHeight = actualImageHeight * this.#scale;
    if (this.classList.contains('nika-zoom-full')) {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const centerX = wrapperRect.left + wrapperRect.width / 2;
      const centerY = wrapperRect.top + wrapperRect.height / 2;
      /* реалното съдържание вместо целия файл (белите полета не броят) */
      const box = this._nikaContentBox;
      let effW = actualImageWidth, effH = actualImageHeight, offX = 0, offY = 0;
      if (box) {
        effW = (box.fRight - box.fLeft) * actualImageWidth;
        effH = (box.fBot - box.fTop) * actualImageHeight;
        offX = ((box.fLeft + box.fRight) / 2 - 0.5) * actualImageWidth;
        offY = ((box.fTop + box.fBot) / 2 - 0.5) * actualImageHeight;
      }
      const sW = effW * this.#scale;
      const sH = effH * this.#scale;
      /* съдържание по-малко от екрана по оста → центрирано, без влачене */
      const xC = (vw / 2 - centerX) / this.#scale - offX;
      const yC = (vh / 2 - centerY) / this.#scale - offY;
      return {
        xMin: sW > vw ? Math.min((vw - sW / 2 - centerX) / this.#scale - offX, xC) : xC,
        xMax: sW > vw ? Math.max((sW / 2 - centerX) / this.#scale - offX, xC) : xC,
        yMin: sH > vh ? Math.min((vh - sH / 2 - centerY) / this.#scale - offY, yC) : yC,
        yMax: sH > vh ? Math.max((sH / 2 - centerY) / this.#scale - offY, yC) : yC,
      };
    }
    const maxX = Math.max(0, scaledImageWidth - wrapperRect.width) / 2 / this.#scale;
    const maxY = Math.max(0, scaledImageHeight - wrapperRect.height) / 2 / this.#scale;
    return { xMin: -maxX, xMax: maxX, yMin: -maxY, yMax: maxY };
  }

  /* NIKA: максималното вертикално изместване при текущия мащаб */
  #maxTranslateY() {
    const wrapperRect = this.getBoundingClientRect();
    if (!wrapperRect.height || !this.#image) return 0;
    let naturalWidth = this.#image.naturalWidth;
    let naturalHeight = this.#image.naturalHeight;
    if (!(naturalWidth > 0 && naturalHeight > 0)) {
      naturalWidth = wrapperRect.width;
      naturalHeight = wrapperRect.width;
    }
    const imageAspectRatio = naturalWidth / naturalHeight;
    const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;
    let actualImageHeight;
    if (imageAspectRatio > wrapperAspectRatio) {
      actualImageHeight = wrapperRect.width / imageAspectRatio;
    } else {
      actualImageHeight = wrapperRect.height;
    }
    const verticalOverflow = Math.max(0, actualImageHeight * this.#scale - wrapperRect.height);
    return verticalOverflow / 2 / this.#scale;
  }

  /* NIKA: плавно прибиране на зума (Amazon edge release) */
  #releaseZoom() {
    this.#animatedExit();
  }

  /* NIKA: плавен изход — снимката се връща до ТОЧНИЯ си вид в списъка
     (запомнен при влизането), после layout смяната е пиксел-точна */
  #animatedExit() {
    if (this._nikaExiting) return;
    if (!this.classList.contains('nika-zoom-full')) {
      this.#exitFull();
      return;
    }
    this._nikaExiting = true;
    const target = this._nikaEntry || { s: MIN_ZOOM, x: 0, y: 0 };
    this.classList.add('nika-zoom-release');
    this.#isPinching = true; /* без кламп по време на анимацията */
    this.#isDragging = false;
    this.#hasManualZoom = false;
    this.#scale = target.s;
    this.#translate.x = target.x;
    this.#translate.y = target.y;
    this.#requestUpdateTransform();
    setTimeout(() => {
      this.classList.remove('nika-zoom-release');
      this.#isPinching = false;
      this.#scale = INITIAL_ZOOM;
      this.#translate.x = 0;
      this.#translate.y = 0;
      this.#exitFull();
      this._nikaExiting = false;
    }, 400);
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (event.touches.length < 2) this.#isPinching = false;
    if (event.touches.length === 0) {
      /* NIKA: flick-изход САМО при истинско рязко драсване — кратък
         контакт, дълго, БЪРЗО и ПРАВО движение. Бързото разглеждане
         (криви/по-дълги/по-бавни движения) не затваря. */
      const endTouch = event.changedTouches && event.changedTouches[0];
      if (this.#scale > 1.01 && this.#hasDraggedBeyondThreshold && endTouch && this._nikaDragStart) {
        const dt = performance.now() - this._nikaDragStart;
        const disp = getDistance(endTouch, this.#startPosition);
        const straight = this._nikaPathLen > 0 ? disp / this._nikaPathLen : 0;
        if (dt < FLICK_MAX_MS && disp > FLICK_MIN_PX && disp / dt > FLICK_MIN_VELOCITY && straight > FLICK_MIN_STRAIGHT) {
          this.#isDragging = false;
          this.#hasDraggedBeyondThreshold = false;
          this.#releaseZoom();
          return;
        }
      }

      this.#isDragging = false;

      /* NIKA: пуснат пръст с опънат ластик → пружиниране обратно */
      if (this._nikaRubberY) {
        this._nikaRubberY = 0;
        this.classList.add('nika-rubber-back');
        setTimeout(() => this.classList.remove('nika-rubber-back'), 320);
      }

      this.#requestUpdateTransform();

      this.#hasDraggedBeyondThreshold = false;

      /* NIKA: отпинчване до 1x връща снимката в списъка */
      if (this.#scale <= 1.01 && this.classList.contains('nika-zoom-full')) this.#animatedExit();
    }
  };

  /**
   * Constrain image translation to keep it within the viewport
   */
  #constrainTranslation() {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    if (!containerWidth || !containerHeight || !this.#image) return;

    // Keep scale between MIN_ZOOM (1) and MAX_ZOOM (5)
    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);

    /* NIKA: по време на активен pinch НЕ клампваме — midpoint компенсацията
       държи точката под пръстите; границите се прилагат при пускане */
    if (this.#isPinching) {
      this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
      this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
      this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
      return;
    }

    // At minimum zoom (1x), the full image should be visible with no dragging allowed
    if (this.#scale <= MIN_ZOOM) {
      this.#translate.x = 0;
      this.#translate.y = 0;
      return;
    }

    const bounds = this.#nikaTranslateBounds();
    this.#translate.x = clamp(this.#translate.x, bounds.xMin, bounds.xMax);
    this.#translate.y = clamp(this.#translate.y, bounds.yMin, bounds.yMax);

    /* NIKA: ластичният overscroll се добавя само ВИЗУАЛНО по време на
       влачене — състоянието остава клампнато, при пускане touchend
       пружинира обратно (клас nika-rubber-back) */
    if (this.#isDragging && this._nikaRubberY) {
      this.#translate.y += this._nikaRubberY;
    }

    // Apply final transforms to CSS
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  }

  /**
   * Request an animation frame to update the transform
   */
  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  /**
   * Cancel any pending animation frame
   */
  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;

    this.#constrainTranslation();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  };

  /**
   * Reset zoom to default state (1.5x scale, centered position)
   * Called when zoom is exited/closed
   */
  #resetZoom = () => {
    // Reset scale and translation to defaults
    this.#scale = INITIAL_ZOOM;
    this.#startScale = INITIAL_ZOOM;
    this.#translate.x = 0;
    this.#translate.y = 0;

    // Reset gesture state to prevent interference on next zoom open
    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;
    this.#hasDraggedBeyondThreshold = false;

    this.#exitFull();

    // Update CSS properties to reflect reset state
    this.style.setProperty('--drag-zoom-scale', INITIAL_ZOOM.toString());
    this.style.setProperty('--drag-zoom-translate-x', '0px');
    this.style.setProperty('--drag-zoom-translate-y', '0px');
  };

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

/**
 * Calculate distance between two points or touches
 * @param {Point | Touch} point1 - First point or touch
 * @param {Point | Touch} point2 - Second point or touch
 * @returns {number} Distance between the points
 */
function getDistance(point1, point2) {
  // Handle both Point objects (x, y) and Touch objects (clientX, clientY)
  const x1 = /** @type {Point} */ (point1).x ?? /** @type {Touch} */ (point1).clientX;
  const y1 = /** @type {Point} */ (point1).y ?? /** @type {Touch} */ (point1).clientY;
  const x2 = /** @type {Point} */ (point2).x ?? /** @type {Touch} */ (point2).clientX;
  const y2 = /** @type {Point} */ (point2).y ?? /** @type {Touch} */ (point2).clientY;

  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {HTMLElement} ZoomDialogElement
 * @property {Function} close - Method to close the zoom dialog
 */
